import { useState } from 'react';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { useContinueFresh } from './ContinueFreshContext';

const MAX_PROVIDER_TEXT = 400;

/**
 * Safeguard banner (Try again / Continue fresh) and the "Continued in" forward
 * card. All text is rendered as React text nodes; the provider message is
 * untrusted and only length-capped.
 */
export function ContinueFreshPanel() {
  const cf = useContinueFresh();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  if (!cf) return null;

  if (cf.successor) {
    return (
      <div className="continue-fresh-card" role="status">
        <span>Continued in</span>
        <button type="button" className="continue-fresh-card__link" onClick={cf.onFollowSuccessor}>
          {cf.successor.label ?? 'New chat'} <ArrowRight size={13} aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (!cf.flag || cf.isLoading) return null;

  const repeated = cf.flag.count >= 2;

  async function handleContinue() {
    setBusy(true);
    setFailure(null);
    const outcome = await cf!.onContinueFresh();
    setBusy(false);
    if (outcome === 'rate_limited') setFailure('Too many fresh starts just now. Wait a few minutes and try again.');
    else if (outcome === 'failed') setFailure('A fresh chat could not be started. Try again in a moment.');
  }

  return (
    <div className="continue-fresh-banner" role="alert">
      <div className="continue-fresh-banner__text">
        <ShieldAlert size={14} aria-hidden="true" />
        <span>{cf.flag.message.slice(0, MAX_PROVIDER_TEXT)}</span>
      </div>
      <div className="continue-fresh-banner__actions">
        <button
          type="button"
          className={repeated ? 'continue-fresh-btn' : 'continue-fresh-btn continue-fresh-btn--primary'}
          onClick={cf.onTryAgain}
          disabled={busy}
        >
          Try again
        </button>
        <button
          type="button"
          className={repeated ? 'continue-fresh-btn continue-fresh-btn--primary' : 'continue-fresh-btn'}
          onClick={() => { void handleContinue(); }}
          disabled={busy}
        >
          {busy ? 'Starting…' : 'Continue fresh'}
        </button>
      </div>
      {failure && <div className="continue-fresh-banner__failure">{failure}</div>}
    </div>
  );
}
